import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agentsApi } from '@/lib/agents';
import { AppShell } from '@/components/app/AppShell';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { modelTierLabel } from '@/lib/model-label';
import { SaveResultButton } from '@/components/app/SaveResultButton';
import { ResultFooter } from '@/components/app/ResultFooter';
import { OutputLanguageSelect } from '@/components/app/OutputLanguageSelect';
import { HelpHint } from '@/components/app/HelpHint';
import { ExampleChips } from '@/components/app/ExampleChips';
import { LanguageCode, defaultOutputLanguage } from '@/i18n/languages';
import { helpFor, pickLang } from '@/lib/agentHelp';

export function ResearchPage() {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [question, setQuestion] = useState('');
  const [language, setLanguage] = useState<LanguageCode>(defaultOutputLanguage(i18n.language));

  const run = useMutation({
    mutationFn: () => agentsApi.research(wsId!, { question, language }),
  });

  return (
    <AppShell
      title={t('research.title', { defaultValue: 'Deep Research' })}
      description={t('research.subtitle', {
        defaultValue: 'Ask a complex question — the agent plans sub-questions, gathers evidence across your corpus, and writes one structured, cited report.',
      }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-5">
        <HelpHint page="research" />

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1 sm:col-start-2">
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('compose.language', { defaultValue: 'Output language' })}</label>
            <OutputLanguageSelect value={language} onChange={setLanguage} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-900 mb-1">{t('research.question', { defaultValue: 'Research question' })}</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                if (question.trim() && !run.isPending) run.mutate();
              }
            }}
            placeholder={`${pickLang(helpFor('research')?.placeholder, i18n.language)} ${t('help.enter_hint', { defaultValue: '· ↩ to run, ⇧↩ for a new line.' })}`}
            rows={3}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </div>

        {!question.trim() && <ExampleChips page="research" onPick={setQuestion} />}

        <div className="flex justify-end">
          <button
            onClick={() => run.mutate()}
            disabled={!question.trim() || run.isPending}
            className="btn-press px-4 py-2 text-sm disabled:opacity-40"
          >
            {run.isPending ? t('research.running', { defaultValue: 'Researching…' }) : t('research.submit', { defaultValue: 'Run deep research' })}
          </button>
        </div>

        {run.isError && (
          <div className="card-paper p-4 text-sm text-warn-600">
            {t('common.action_failed', { defaultValue: 'Something went wrong. Please try again.' })}
          </div>
        )}

        {run.data && (
          <article className="card-paper p-5">
            <div className="text-xs text-ink-500 mb-3 flex items-center justify-between">
              <span>{run.data.subQuestions.length} {t('research.subquestions', { defaultValue: 'sub-questions' })} · {run.data.retrievedChunkIds.length} {t('research.sources', { defaultValue: 'sources' })}</span>
              <span>{modelTierLabel(run.data.model)} · {run.data.inputTokens + run.data.outputTokens} tok · ¢{run.data.costCents}</span>
            </div>

            {run.data.subQuestions.length > 0 && (
              <div className="mb-4 rounded-lg bg-paper-50 border border-ink-100 px-3 py-2.5">
                <div className="text-[11px] uppercase tracking-[0.14em] text-accent-600 font-semibold mb-1">
                  {t('research.plan', { defaultValue: 'Research plan' })}
                </div>
                <ol className="list-decimal pl-5 space-y-0.5 text-[13px] text-ink-700">
                  {run.data.subQuestions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ol>
              </div>
            )}

            <CitedMarkdown content={run.data.answer} wsId={wsId} />

            <ResultFooter wsId={wsId!} citedChunkIds={run.data.citedChunkIds} askSeed={question}>
              <SaveResultButton
                workspaceId={wsId!}
                kind="research"
                title={question}
                output={run.data.answer}
                input={{ question, language }}
                citedChunkIds={run.data.citedChunkIds}
                model={run.data.model}
                costCents={run.data.costCents}
              />
            </ResultFooter>
          </article>
        )}
      </div>
    </AppShell>
  );
}
